import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-offwhite py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12">
          <Link to="/" className="inline-block mb-8 text-gray-600 hover:text-gray-900 transition-colors">
            ← Back to Home
          </Link>
          <h1 className="text-5xl md:text-6xl font-light text-gray-900 mb-4 text-smooth">
            Privacy Policy
          </h1>
          <p className="text-gray-500 text-sm">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        <div className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-light text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Welcome to Instnce, a cryptocurrency-enabled platform that allows users to trade digital content. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you 
              use our mobile application and services, including cryptocurrency transactions. Please read this policy 
              carefully to understand our practices regarding your data and how we will treat it.
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed">
              By using our Service, you agree to the collection and use of information in accordance with this policy. 
              If you do not agree with our policies and practices, do not use our Service.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-light text-gray-900 mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-normal text-gray-800 mb-3 mt-6">2.1 Personal Information</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="text-gray-700 mb-4 space-y-2 ml-6">
              <li>Register for an account or complete identity verification (KYC)</li>
              <li>Create or share content</li>
              <li>Participate in transactions</li>
              <li>Connect cryptocurrency wallets</li>
              <li>Contact us for support</li>
              <li>Connect social media accounts</li>
              <li>Provide tax or regulatory information</li>
            </ul>

            <h3 className="text-xl font-normal text-gray-800 mb-3 mt-6">2.2 Cryptocurrency and Wallet Information</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              In connection with cryptocurrency transactions on our platform, we collect:
            </p>
            <ul className="text-gray-700 mb-4 space-y-2 ml-6">
              <li>Public wallet addresses and associated transaction history</li>
              <li>Cryptocurrency transaction amounts, timestamps, and blockchain identifiers</li>
              <li>Cryptocurrency asset holdings and balances (as visible on public blockchains)</li>
              <li>Network and blockchain information (e.g., Ethereum, Solana, etc.)</li>
              <li>Smart contract interaction data</li>
            </ul>
            <p className="text-gray-700 mb-4 leading-relaxed">
              <strong>Note:</strong> We do not collect private keys, seed phrases, or other sensitive wallet credentials. 
              These remain under your exclusive control and are never transmitted to our servers.
            </p>

            <h3 className="text-xl font-normal text-gray-800 mb-3 mt-6">2.3 Usage Data</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              We automatically collect certain information when you use our Service, including:
            </p>
            <ul className="text-gray-700 mb-4 space-y-2 ml-6">
              <li>Device information and identifiers</li>
              <li>App usage patterns and interactions</li>
              <li>Transaction history and trading activity</li>
              <li>Content engagement metrics</li>
              <li>Network information and IP address</li>
              <li>Browser or app version information</li>
            </ul>

            <h3 className="text-xl font-normal text-gray-800 mb-3 mt-6">2.4 Financial and Compliance Information</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              To comply with applicable laws and regulations (including anti-money laundering (AML) and know-your-customer (KYC) requirements), we may collect:
            </p>
            <ul className="text-gray-700 mb-4 space-y-2 ml-6">
              <li>Government-issued identification documents</li>
              <li>Proof of address</li>
              <li>Tax identification numbers or Social Security Numbers (where required)</li>
              <li>Source of funds documentation</li>
              <li>Financial account information for fiat currency transactions</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-light text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              We use the collected information for various purposes, including:
            </p>
            <ul className="text-gray-700 mb-4 space-y-2 ml-6">
              <li>To provide, maintain, and improve our Service and cryptocurrency trading functionality</li>
              <li>To process cryptocurrency and fiat currency transactions</li>
              <li>To verify your identity and comply with KYC/AML regulatory requirements</li>
              <li>To manage your account, wallet connections, and transaction history</li>
              <li>To communicate with you about your account, transactions, and our services</li>
              <li>To provide customer support and respond to inquiries</li>
              <li>To detect, prevent, and address fraud, security threats, money laundering, and other illegal activities</li>
              <li>To analyze usage patterns, market trends, and optimize user experience</li>
              <li>To comply with legal obligations, including tax reporting and regulatory requirements</li>
              <li>To enforce our terms of service and protect our rights and the rights of our users</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-light text-gray-900 mb-4">4. Sharing Your Information</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              We may share your information in the following circumstances:
            </p>
            <ul className="text-gray-700 mb-4 space-y-2 ml-6">
              <li>With your explicit consent</li>
              <li>To comply with legal obligations, regulatory requirements, or respond to lawful requests from government authorities, courts, or regulatory bodies</li>
              <li>With cryptocurrency exchanges, blockchain analytics providers, and other service providers who assist in operating our platform</li>
              <li>With compliance and identity verification service providers (KYC/AML)</li>
              <li>To detect, prevent, or address fraud, security, or technical issues</li>
              <li>To protect our rights, privacy, safety, or property, or that of our users</li>
              <li>In connection with business transfers or mergers</li>
              <li>With analytics and advertising partners (as aggregated or anonymized data only)</li>
            </ul>
            <p className="text-gray-700 mb-4 leading-relaxed">
              <strong>Regulatory Compliance:</strong> We may be required to share information with tax authorities, 
              financial regulators, law enforcement, or other governmental agencies to comply with applicable laws, 
              including but not limited to cryptocurrency regulations, tax reporting requirements, and AML/KYC obligations.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-light text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              We implement industry-standard technical and organizational measures to protect your personal information, 
              including encryption, secure socket layer (SSL) technology, multi-factor authentication, and regular security audits. 
              We use secure storage systems and restrict access to personal information to authorized personnel only.
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed">
              However, no method of transmission over the Internet or electronic storage is completely secure. 
              Blockchain transactions are immutable and public by nature. While we take reasonable precautions, 
              we cannot guarantee absolute security. You are responsible for maintaining the security of your wallet 
              credentials, passwords, and account access information.
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed">
              <strong>Important:</strong> We never have access to your private keys or seed phrases. You are solely 
              responsible for securing your cryptocurrency wallet credentials. If your private keys are lost or compromised, 
              we cannot recover your funds.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-light text-gray-900 mb-4">6. Your Rights</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul className="text-gray-700 mb-4 space-y-2 ml-6">
              <li>The right to access and receive a copy of your personal data</li>
              <li>The right to rectify inaccurate or incomplete data</li>
              <li>The right to request deletion of your personal data</li>
              <li>The right to restrict or object to certain processing activities</li>
              <li>The right to data portability</li>
              <li>The right to withdraw consent at any time</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-light text-gray-900 mb-4">7. Third-Party Services and Blockchain Networks</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Our Service integrates with third-party services, including:
            </p>
            <ul className="text-gray-700 mb-4 space-y-2 ml-6">
              <li>Cryptocurrency wallet providers and browser extensions</li>
              <li>Blockchain networks (e.g., Ethereum, Solana, Polygon) which are public and decentralized</li>
              <li>Cryptocurrency exchanges and payment processors</li>
              <li>Identity verification and KYC/AML service providers</li>
              <li>Cloud storage and infrastructure providers</li>
              <li>Analytics and security service providers</li>
            </ul>
            <p className="text-gray-700 mb-4 leading-relaxed">
              We are not responsible for the privacy practices of these third parties. Transactions conducted on 
              public blockchains are visible to anyone and cannot be deleted or modified due to the immutable nature 
              of blockchain technology. We encourage you to review third-party privacy policies before providing any information.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-light text-gray-900 mb-4">8. Data Retention and Deletion</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              We retain your personal information for as long as necessary to provide our services, comply with legal 
              obligations, resolve disputes, and enforce our agreements. Cryptocurrency transaction data may be retained 
              for extended periods as required by law, including tax reporting and regulatory compliance requirements 
              (typically 5-7 years, or longer if required by applicable regulations).
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed">
              <strong>Blockchain Data:</strong> Data stored on public blockchains cannot be deleted or modified. 
              While we may delete your account information from our systems, blockchain transaction records remain 
              permanently on the distributed ledger and are publicly accessible.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-light text-gray-900 mb-4">9. International Data Transfers</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of residence. 
              These countries may have data protection laws that differ from those in your country. By using our Service, 
              you consent to the transfer of your information to these countries. We implement appropriate safeguards, 
              such as standard contractual clauses, to protect your information when transferred internationally.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-light text-gray-900 mb-4">10. Regulatory Disclosures and Compliance</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              As a cryptocurrency-enabled platform, we are subject to various regulatory requirements that may require 
              us to collect, retain, and disclose certain information. This includes:
            </p>
            <ul className="text-gray-700 mb-4 space-y-2 ml-6">
              <li>Anti-Money Laundering (AML) and Counter-Terrorism Financing (CTF) regulations</li>
              <li>Know Your Customer (KYC) requirements</li>
              <li>Tax reporting and compliance obligations</li>
              <li>Securities and financial services regulations</li>
              <li>Data breach notification requirements</li>
            </ul>
            <p className="text-gray-700 mb-4 leading-relaxed">
              We may be required to report suspicious transactions or share information with regulatory authorities 
              without your consent as mandated by applicable law.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-light text-gray-900 mb-4">11. Children's Privacy</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Our Service is not intended for individuals under the age of 18. We do not knowingly collect 
              personal information from children. If you become aware that a child has provided us with personal 
              information, please contact us, and we will take steps to delete such information.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-light text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              We may update our Privacy Policy from time to time to reflect changes in our practices, technology, 
              legal requirements, or other factors. We will notify you of material changes by posting the new Privacy 
              Policy on this page and updating the "Last updated" date. For significant changes, we may also provide 
              notice via email or through our Service. You are advised to review this Privacy Policy periodically 
              for any changes.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-light text-gray-900 mb-4">13. Contact Us</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
            </p>
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <p className="text-gray-900">
                <strong>Email:</strong> founders@instnce.xyz
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;

